import React, { useContext, useEffect, useState, useRef } from "react";
import { Box } from "@mui/material";
import {
    Text,
    FlexContainer,
    Toast,
    StandardModal,
    StandardModalActionButton,
    StandardModalActionGroup,
    StandardModalSelect
} from "src/components";
import { LeadershipContext } from "./LeadershipContext";
import TwoImage from "src/img/new/chats/leadership/LeadershipRoleImage.png";
import Users from "src/img/new/users.svg";
import LeadershipTable from "./LeadershipTable";
import {
    InputField,
    AssignmentCard,
    UserBox, UserIconContainer
} from "./Leadership.style";
import { useSetEmployeeLeadershipRoles, useSaveAssignedLeadershipRoles } from "../../hooks";
import { fetchUsers, submitCreateRole, submitUpdateRole, handleDeleteRole } from "./LeadershipAPI";
import { FILTER_BY } from "./constants";
import { customNames } from "src/utils/helpers";

const LeadershipRoleModal = ({ showDialog, handleClose, token, refetchRoles }) => {
    const isMounted = useRef(true);
    const [isLoading, setIsLoading] = useState(true);
    const [employeeUsers, setEmployeeUsers] = useState([]);
    const [clientUsers, setClientUsers] = useState([]);
    const [selectedUsers, setSelectedUsers] = useState([]);
    const [filterByOption, setFilterByOption] = useState(FILTER_BY.EMPLOYEE);
    const [roleName, setRoleName] = useState("");

    const { createLeadershipRole, updateLeadershipRole, leadershipRoleToEdit, deleteLeadershipRole, isFormCreate } = useContext(LeadershipContext);
    const { assignLeadershipRoles } = useSetEmployeeLeadershipRoles();
    const { saveAssignedLeadershipRoles } = useSaveAssignedLeadershipRoles();
    const [totalEmployeesAssigned, setTotalEmployeesAssigned] = useState(0);
    const [totalClientsAssigned, setTotalClientsAssigned] = useState(0);

    const users = filterByOption === FILTER_BY.EMPLOYEE ? employeeUsers : clientUsers;

    const allEmployeesSelected =
        employeeUsers.length > 0 &&
        employeeUsers.every(user => selectedUsers.includes(user.uuid));

    const allClientsSelected =
        clientUsers.length > 0 &&
        clientUsers.every(user => selectedUsers.includes(user.uuid));

    
    useEffect(() => {
        isMounted.current = true;
        return () => { isMounted.current = false; };
    }, []);

    useEffect(() => {
        if (showDialog) {
            setIsLoading(true);

            fetchUsers(filterByOption, token).then(fetched => {
                if (!isMounted.current) return;
            
                const mappedUsers = fetched.map(user => {
                    const assignedEmp = leadershipRoleToEdit?.assignedEmployees?.find(e => e.employeeUuid === user.uuid);
                    const assignedClient = leadershipRoleToEdit?.assignedClients?.find(c => c.employeeUuid === user.uuid);

                    return {
                        ...user,
                        uuid: user.uuid, 
                        isVisibleToEmployees: assignedEmp?.isVisibleToEmployees || assignedClient?.isVisibleToEmployees || false,
                        isVisibleToClients: assignedEmp?.isVisibleToClients || assignedClient?.isVisibleToClients || false,
                    };
                });

                if (filterByOption === FILTER_BY.EMPLOYEE) setEmployeeUsers(mappedUsers);
                else setClientUsers(mappedUsers);

                if (!isFormCreate && leadershipRoleToEdit) {
                    const preSelected = Array.from(new Set([
                        ...leadershipRoleToEdit.assignedEmployees.map(e => e.employeeUuid),
                        ...leadershipRoleToEdit.assignedClients.map(c => c.employeeUuid),
                    ]));
                    setSelectedUsers(preSelected);
                }


                setIsLoading(false);
            });
        }
    }, [showDialog, filterByOption, token, leadershipRoleToEdit, isFormCreate]);


    useEffect(() => {
        if (!isFormCreate && leadershipRoleToEdit) {
            setRoleName(leadershipRoleToEdit.name);

            // Use totals from backend directly
            const { totalEmployees, totalClients } = leadershipRoleToEdit;
            setTotalEmployeesAssigned(totalEmployees);
            setTotalClientsAssigned(totalClients);

            // Pre-select users without duplication
            const preSelected = Array.from(new Set([
                ...leadershipRoleToEdit.assignedEmployees.map(e => e.employeeUuid),
                ...leadershipRoleToEdit.assignedClients.map(c => c.employeeUuid),
            ]));
            setSelectedUsers(preSelected);

            // Map visibility
            setEmployeeUsers(prev =>
                prev.map(u => ({
                    ...u,
                    isVisibleToEmployees: leadershipRoleToEdit.assignedEmployees.some(ae => ae.employeeUuid === u.uuid && ae.isVisibleToEmployees),
                }))
            );

            setClientUsers(prev =>
                prev.map(u => ({
                    ...u,
                    isVisibleToClients: leadershipRoleToEdit.assignedClients.some(ac => ac.employeeUuid === u.uuid && ac.isVisibleToClients),
                }))
            );
        }
    }, [isFormCreate, leadershipRoleToEdit]);


    const handleInputChange = e => setRoleName(e.target.value);

    const handleUserToggle = (userId, isSelected) => {
        setSelectedUsers(prev => {
            const newSelected = isSelected
                ? [...prev, userId]
                : prev.filter(id => id !== userId);

            // Update counts dynamically
            setTotalEmployeesAssigned(newSelected.filter(id => employeeUsers.some(u => u.uuid === id)).length);
            setTotalClientsAssigned(newSelected.filter(id => clientUsers.some(u => u.uuid === id)).length);

            return newSelected;
        });

        // Only clear visibility if unselecting
        if (!isSelected) {
            setEmployeeUsers(prev =>
                prev.map(u =>
                    u.uuid === userId
                        ? { ...u, isVisibleToEmployees: false, isVisibleToClients: false }
                        : u
                )
            );
            setClientUsers(prev =>
                prev.map(u =>
                    u.uuid === userId
                        ? { ...u, isVisibleToEmployees: false, isVisibleToClients: false }
                        : u
                )
            );
        }
    };

    const handleAllSwitch = (type, checked) => {
        const targetUsers = type === FILTER_BY.CLIENT ? clientUsers : employeeUsers;
        const allUserIds = targetUsers.map(u => u.uuid);

        setSelectedUsers(prev => {
            let newSelected;
            if (checked) {
                newSelected = Array.from(new Set([...prev, ...allUserIds]));
            } else {
                newSelected = prev.filter(id => !allUserIds.includes(id));
            }

            setTotalEmployeesAssigned(newSelected.filter(id => employeeUsers.some(u => u.uuid === id)).length);
            setTotalClientsAssigned(newSelected.filter(id => clientUsers.some(u => u.uuid === id)).length);

            return newSelected;
        });
    };

    const handleVisibilityToggle = (userUuid, key) => {
        if (!selectedUsers.includes(userUuid)) {
            Toast.error("Please assign the user first before changing visibility.");
            return;
        }

        const setter = filterByOption === FILTER_BY.EMPLOYEE ? setEmployeeUsers : setClientUsers;
        setter(prev =>
            prev.map(u => 
                u.uuid === userUuid ? { ...u, [key]: !u[key] } : u
            )
        );

        assignLeadershipRoles(key, userUuid);
    };


    const submitForm = () => {
        setIsLoading(true);
        if (isFormCreate) {
            submitCreateRole({ roleName, selectedUsers, employeeUsers, clientUsers, createLeadershipRole, saveAssignedLeadershipRoles, refetchRoles, token, handleClose });
        } else {
            submitUpdateRole({ roleName, selectedUsers, users, leadershipRoleToEdit, updateLeadershipRole, saveAssignedLeadershipRoles, refetchRoles, token, handleClose });
        }
        setIsLoading(false);
    };

    const handleDelete = () => handleDeleteRole({ leadershipRoleToEdit, deleteLeadershipRole });

    return (
        <StandardModal
            open={showDialog}
            onClose={handleClose}
            title={`${isFormCreate ? "New" : "Edit"} Leadership Role`}
            widthMode="min(72rem, calc(100vw - 2rem))"
            footer={(
                <div style={{ display: "flex", justifyContent: "space-between", width: "100%", gap: "0.75rem", flexWrap: "wrap" }}>
                    {!isFormCreate ? (
                        <StandardModalActionButton $variant="danger" width="16rem" onClick={handleDelete}>
                            Delete Leadership Role
                        </StandardModalActionButton>
                    ) : <div />}
                    <StandardModalActionGroup $pushRight>
                        <StandardModalActionButton onClick={handleClose}>Cancel</StandardModalActionButton>
                        <StandardModalActionButton $variant="primary" onClick={submitForm}>Save</StandardModalActionButton>
                    </StandardModalActionGroup>
                </div>
            )}
        >
            <Box>
                <FlexContainer direction="row" justifyContent="space-between" marginTop="0" style={{ gap: "1rem", flexWrap: "wrap" }}>
                    <div style={{ maxWidth: "18.875rem", width: "100%" }}>
                        <Text align="left" weight="700" size="16px" margin="0 0 0.75rem 0">{isFormCreate ? "Create" : "Edit"} Leadership Role Name</Text>
                        <InputField value={roleName} onChange={handleInputChange} placeholder="Eg: Customer Support" placeholderAlignment="left" />
                        <Text align="left" margin="1.3125rem 0 0 0" size="14px" color="#8E4EB4">
                            <span style={{ fontSize: "14px", fontWeight: 600 }}>Leadership Roles help your team know who to contact. </span><br /><br />
                            Assign a role to make key people clearly visible in chat across the app and web. 
                            Based on your settings, {customNames.employees} or {customNames.clients} can easily identify 
                            and message the right person for support, guidance, or leadership.
                        </Text>
                    </div>
                    <FlexContainer direction="row">
                        <img src={TwoImage} alt="one" width="100%" height="400px"/>
                    </FlexContainer>
                </FlexContainer>

                <FlexContainer direction="row" justifyContent="space-between" marginTop="1rem" marginBottom="1rem">
                    <Text weight="700" size="1rem" color="#000000">Assign Users to Leadership Role</Text>
                    <StandardModalSelect width="12.5rem" value={filterByOption} onChange={e => setFilterByOption(e.target.value)}>
                        <option value={FILTER_BY.EMPLOYEE}>{customNames.employees}</option>
                        <option value={FILTER_BY.CLIENT}>{customNames.clients}</option>
                    </StandardModalSelect>
                </FlexContainer>

                <AssignmentCard>
                    <FlexContainer direction="row" justifyContent="space-start" alignItems="center" gap="24px">
                        <Text weight="500" size="1rem" color="#4B5155">Selected Users:</Text>
                        <FlexContainer direction="row" gap="1rem">
                            <UserBox>
                                <UserIconContainer><img src={Users} alt="employees" style={{ width: 16, height: 16 }} /></UserIconContainer>
                                {totalEmployeesAssigned} {customNames.employees}
                            </UserBox>
                            <UserBox>
                                <UserIconContainer><img src={Users} alt="clients" style={{ width: 16, height: 16 }} /></UserIconContainer>
                                {totalClientsAssigned} {customNames.clients}
                            </UserBox>
                        </FlexContainer>
                    </FlexContainer>

                    <FlexContainer direction="row" justifyContent="space-between" alignItems="center">
                        <Text weight="500" size="1rem" color="#4B5155">
                            Assign {filterByOption === FILTER_BY.EMPLOYEE ? customNames.employees : customNames.clients}
                        </Text>
                        <Text weight="600" size="1rem" color="#000000">
                            {selectedUsers.filter(id => 
                                (filterByOption === FILTER_BY.EMPLOYEE ? employeeUsers : clientUsers).some(u => u.uuid === id)
                            ).length} {filterByOption === FILTER_BY.EMPLOYEE ? customNames.employees : customNames.clients} Assigned
                        </Text>
                    </FlexContainer>

                    <LeadershipTable
                        usersList={users}
                        selectedUserGroupType={filterByOption === FILTER_BY.CLIENT ? FILTER_BY.CLIENT : FILTER_BY.EMPLOYEE}
                        allEmployeesSelected={allEmployeesSelected}
                        allClientsSelected={allClientsSelected}
                        handleAllSwitch={handleAllSwitch}
                        selectedUsers={selectedUsers}
                        onUserToggle={handleUserToggle}
                        onVisibilityToggle={handleVisibilityToggle}
                        isLoading={isLoading}
                    />
                </AssignmentCard>

            </Box>
        </StandardModal>
    );
};

export default LeadershipRoleModal;
